import React, { useState } from "react";
import {
  Book,
  Palette,
  Layout,
  MessageSquare,
  Terminal,
  Check,
  X,
  Menu,
  ArrowRight,
  Image as ImageIcon,
  Layers,
} from "lucide-react";

import WebsiteReview from "./components/WebsiteReview";

// --- BRAND DATA ---
const BRAND = {
  name: "SIMPLER // STRATEGY",
  version: "3.1", // Update: Mobile Logo Fix
  sections: [
    { id: "core", label: "Philosophy", icon: <Book size={18} /> },
    { id: "voice", label: "Voice & Tone", icon: <MessageSquare size={18} /> },
    { id: "logo", label: "Logo System", icon: <ImageIcon size={18} /> },
    { id: "visuals", label: "Visual Identity", icon: <Palette size={18} /> },
    { id: "ui", label: "UI System", icon: <Layout size={18} /> },
    { id: "vocab", label: "Dictionary", icon: <Terminal size={18} /> },
    { id: "review", label: "Website Review", icon: <Layers size={18} /> },
  ],
};

// --- COMPONENTS ---

const ColorSwatch = ({ name, hex, usage }) => (
  <div
    style={{
      background: "rgba(255,255,255,0.03)",
      borderRadius: "12px",
      overflow: "hidden",
      border: "1px solid rgba(255,255,255,0.05)",
    }}
  >
    <div
      style={{
        height: "80px",
        background: hex,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <span
        style={{
          background: "rgba(0,0,0,0.5)",
          color: "#fff",
          padding: "4px 8px",
          borderRadius: "4px",
          fontSize: "12px",
          fontFamily: "monospace",
        }}
      >
        {hex}
      </span>
    </div>
    <div style={{ padding: "16px" }}>
      <div style={{ fontWeight: "700", color: "#fff", marginBottom: "4px" }}>
        {name}
      </div>
      <div style={{ fontSize: "13px", color: "#9ca3af" }}>{usage}</div>
    </div>
  </div>
);

const VoiceCard = ({ doText, dontText }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "1px",
      background: "rgba(255,255,255,0.1)",
      borderRadius: "12px",
      overflow: "hidden",
    }}
  >
    <div style={{ background: "rgba(34,197,94,0.05)", padding: "20px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          color: "#22c55e",
          fontWeight: "800",
          fontSize: "11px",
          textTransform: "uppercase",
          marginBottom: "8px",
        }}
      >
        <Check size={14} /> Do This
      </div>
      <div style={{ color: "#fff", fontSize: "15px", fontWeight: "500" }}>
        "{doText}"
      </div>
    </div>
    <div style={{ background: "rgba(239,68,68,0.05)", padding: "20px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          color: "#ef4444",
          fontWeight: "800",
          fontSize: "11px",
          textTransform: "uppercase",
          marginBottom: "8px",
        }}
      >
        <X size={14} /> Don't Do This
      </div>
      <div
        style={{
          color: "#9ca3af",
          fontSize: "15px",
          textDecoration: "line-through",
        }}
      >
        "{dontText}"
      </div>
    </div>
  </div>
);

const Section = ({ title, subtitle, children }) => (
  <section style={{ marginBottom: "80px", animation: "fadeIn 0.4s ease-out" }}>
    <div
      style={{
        marginBottom: "32px",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        paddingBottom: "24px",
      }}
    >
      <h2
        style={{
          fontSize: "32px",
          fontWeight: "800",
          color: "#fff",
          marginBottom: "8px",
          letterSpacing: "-0.02em",
        }}
      >
        {title}
      </h2>
      <p
        style={{
          fontSize: "18px",
          color: "#9ca3af",
          lineHeight: "1.6",
          maxWidth: "700px",
        }}
      >
        {subtitle}
      </p>
    </div>
    {children}
  </section>
);

const MasterIcon = ({ color = "#22c55e" }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
    }}
  >
    <svg
      width="200"
      height="200"
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        filter: `drop-shadow(0 0 10px ${color})`,
        maxWidth: "100%",
        height: "auto",
      }}
    >
      <path
        d="M10 35 L50 10 L90 35"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M70 22 V12 H80 V28"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M28 35 V90"
        stroke={color}
        strokeWidth="8"
        strokeLinecap="square"
      />
      <path
        d="M72 35 V90"
        stroke={color}
        strokeWidth="8"
        strokeLinecap="square"
      />
      <path
        d="M20 90 H36 M64 90 H80 M20 35 H36 M64 35 H80"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
      />
      <line
        x1="15"
        y1="62"
        x2="85"
        y2="62"
        stroke={color}
        strokeWidth="4"
        strokeLinecap="round"
      />
      <rect
        x="15"
        y="52"
        width="5"
        height="20"
        rx="1"
        stroke={color}
        strokeWidth="3"
      />
      <rect
        x="80"
        y="52"
        width="5"
        height="20"
        rx="1"
        stroke={color}
        strokeWidth="3"
      />
    </svg>
  </div>
);

// --- FIXED RESPONSIVE LOGO COMPONENT ---
const SimplerLogo = ({ variant = "brand", color = "#22c55e", style = {} }) => {
  const config = {
    brand: { top: "IMPLER", bot: "EALTH" },
    trainer: { top: "COTT", bot: "OGAN" },
    monogram: { top: ".", bot: "." },
  };
  const text = config[variant] || config.brand;
  const isMonogram = variant === "monogram";

  // Use Clamp for responsive scaling
  const fontSize = "clamp(32px, 8vw, 64px)";
  const iconSize = "clamp(24px, 6vw, 50px)";
  const iconHeight = "clamp(30px, 8vw, 64px)";
  const rowHeight = "clamp(30px, 8vw, 60px)";

  const fontStyle = {
    fontSize: fontSize,
    lineHeight: "0.85",
    fontWeight: "800",
    fontFamily: "'Inter', sans-serif",
    letterSpacing: "-0.03em",
    color: isMonogram ? color : "#fff",
    textTransform: "uppercase",
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMonogram ? "row" : "column",
        alignItems: isMonogram ? "flex-end" : "flex-start",
        gap: isMonogram ? "16px" : "4px",
        justifyContent: "center",
        ...style,
      }}
    >
      {/* TOP ROW (S) */}
      <div style={{ display: "flex", alignItems: "flex-end", height: rowHeight }}>
        <div
          style={{
            width: iconSize,
            height: iconHeight,
            marginRight: "6px",
            position: "relative",
            flexShrink: 0, // Prevent squishing
          }}
        >
          <svg viewBox="0 0 50 64" fill="none" width="100%" height="100%">
            <circle cx="32" cy="8" r="6" stroke={color} strokeWidth="5" />
            <path
              d="M30 18 L15 38 H38 L15 62"
              stroke={color}
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <span style={fontStyle}>{text.top}</span>
      </div>

      {/* BOTTOM ROW (H) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: rowHeight,
          marginTop: isMonogram ? "0" : "4px",
        }}
      >
        <div
          style={{
            width: iconSize,
            height: "100%", // Fill container
            marginRight: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0, // Prevent squishing
          }}
        >
          <svg viewBox="0 0 50 44" fill="none" width="100%" height="auto">
            <rect x="2" y="0" width="6" height="44" rx="2" fill={color} />
            <rect x="10" y="6" width="4" height="32" rx="1" fill={color} />
            <rect x="8" y="20" width="34" height="4" fill={color} />
            <rect x="36" y="6" width="4" height="32" rx="1" fill={color} />
            <rect x="42" y="0" width="6" height="44" rx="2" fill={color} />
          </svg>
        </div>
        <span style={fontStyle}>{text.bot}</span>
      </div>
    </div>
  );
};

export default function App() {
  const [activeTab, setActiveTab] = useState("core");
  const [mobileMenu, setMobileMenu] = useState(false);

  const handleReviewNavigation = (action) => {
    alert(`Action: ${action.type} -> ${action.target}`);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "core":
        return (
          <Section
            title="The Core Philosophy"
            subtitle="Simple foundations. Pain-aware progressions. Built for real life."
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "24px",
                marginBottom: "24px",
              }}
            >
              <div className="card">
                <div className="label">The Why</div>
                <h3>Most people don't need more info.</h3>
                <p>
                  They need a clear starting point. We cut through gadgets,
                  diets, and noise and install the basics that actually move the
                  needle.
                </p>
              </div>

              <div className="card">
                <div className="label">The How (House Stages)</div>
                <h3>Build it like a house.</h3>
                <p>
                  Assessment → Clear Land → Foundation → Structure → Finish →
                  Maintenance. Same order every time. No chaos.
                </p>
              </div>

              <div className="card highlight">
                <div className="label">The Big Rocks</div>
                <h3>The basics win.</h3>
                <p>
                  Steps. Strength. Protein. Sleep. Mobility. Consistency. We
                  focus on what compounds over time.
                </p>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "24px",
                marginBottom: "24px",
              }}
            >
              <div className="card">
                <div className="label">Where / Delivery</div>
                <h3>In-home + Remote.</h3>
                <p>
                  Start in your home (mine or yours), or coach remotely. The
                  system works either way.
                </p>
              </div>

              <div className="card">
                <div className="label">Pain / Correctives</div>
                <h3>Pain-aware by design.</h3>
                <p>
                  If something hurts, we don't "push through." We adjust, clear
                  constraints, and keep progress moving.
                </p>
              </div>
            </div>

            <div
              className="card"
              style={{ borderColor: "rgba(255,255,255,0.1)" }}
            >
              <div className="label">The Community</div>
              <h3>Community without friction.</h3>
              <p>
                We’ll start where people already are (local + YouTube).
                Community comes next — simple, optional, and useful.
              </p>
            </div>
          </Section>
        );

      case "voice":
        return (
          <Section
            title="Voice & Tone"
            subtitle="The Coach, Not The Cheerleader. Calm, authoritative, and economical."
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                marginBottom: "40px",
              }}
            >
              <VoiceCard
                doText="Recovery is the limiter."
                dontText="You need to rest more!"
              />
              <VoiceCard
                doText="System Status: Online"
                dontText="Great job today!"
              />
              <VoiceCard
                doText="Accept Adjustment"
                dontText="Click here to update"
              />
              <VoiceCard
                doText="Awareness beats force."
                dontText="No pain no gain."
              />
            </div>
          </Section>
        );

      case "logo":
        return (
          <Section
            title="Logo System"
            subtitle="The Master Icon & Typographic Lockups."
          >
            <div
              className="card highlight"
              style={{
                padding: "80px 40px", // Adjusted padding for mobile
                background: "#0b0c10",
                marginBottom: "60px",
                border: "1px solid rgba(34,197,94,0.2)",
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <MasterIcon />
              <div
                style={{
                  marginTop: "32px",
                  fontSize: "12px",
                  color: "#22c55e",
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  fontWeight: "700",
                }}
              >
                The Master Icon
              </div>
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#9ca3af",
                textTransform: "uppercase",
                letterSpacing: "2px",
                marginBottom: "24px",
                textAlign: "center",
                borderTop: "1px solid rgba(255,255,255,0.1)",
                paddingTop: "40px",
              }}
            >
              Alternative Lockups (The Waves)
            </div>
            <div style={{ display: "grid", gap: "40px" }}>
              <div
                className="card"
                style={{
                  padding: "40px 20px", // Responsive padding
                  background: "#0b0c10",
                  display: "flex",
                  justifyContent: "center",
                  overflow: "hidden" // Prevent spill
                }}
              >
                <SimplerLogo variant="brand" />
              </div>
              <div
                className="card"
                style={{
                  padding: "40px 20px",
                  background: "#0b0c10",
                  display: "flex",
                  justifyContent: "center",
                  overflow: "hidden"
                }}
              >
                <SimplerLogo variant="trainer" />
              </div>
              <div
                className="card"
                style={{
                  padding: "40px 20px",
                  background: "#0b0c10",
                  display: "flex",
                  justifyContent: "center",
                  overflow: "hidden"
                }}
              >
                <SimplerLogo variant="monogram" />
              </div>
            </div>
          </Section>
        );

      case "visuals":
        return (
          <Section
            title="Visual Identity"
            subtitle="Dark mode by default. Neon signals in the void."
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
                marginBottom: "40px",
              }}
            >
              <ColorSwatch
                name="Deep Obsidian"
                hex="#0b0c10"
                usage="Main Background"
              />
              <ColorSwatch
                name="Emerald Neon"
                hex="#22c55e"
                usage="Brand / Success"
              />
              <ColorSwatch
                name="Amber Glow"
                hex="#f59e0b"
                usage="Warning / Constraint"
              />
              <ColorSwatch
                name="Tech Blue"
                hex="#3b82f6"
                usage="Info / Context"
              />
              <ColorSwatch
                name="Off-White"
                hex="#e5e7eb"
                usage="Primary Text"
              />
            </div>
          </Section>
        );

      case "ui":
        return (
          <Section
            title="UI System"
            subtitle="Layers of glass, signal bars, and monospaced precision."
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "24px",
              }}
            >
              <div className="card">
                <div className="label">The Glass Card</div>
                <p>
                  Background: <code>rgba(17, 19, 24, 0.6)</code>
                </p>
              </div>
              <div className="card">
                <div className="label">Signal Bars</div>
                <div style={{ display: "flex", gap: "4px", marginTop: "12px" }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        height: "8px",
                        background: i < 6 ? "#22c55e" : "rgba(255,255,255,0.1)",
                        borderRadius: "2px",
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </Section>
        );

      case "vocab":
        return (
          <Section
            title="Dictionary"
            subtitle="We rename standard fitness terms to fit our 'Construction' metaphor."
          >
            <div style={{ display: "grid", gap: "12px" }}>
              {[
                { from: "Workout", to: "Session" },
                { from: "Program", to: "Blueprints" },
                { from: "Injury", to: "Signal / Constraint" },
                { from: "Rest Day", to: "Calibration" },
                { from: "Warmup", to: "Prep / Clear Land" },
                { from: "Dashboard", to: "System Snapshot" },
              ].map((word, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px",
                    background: "rgba(255,255,255,0.03)",
                    borderRadius: "8px",
                  }}
                >
                  <span
                    style={{ color: "#9ca3af", textDecoration: "line-through" }}
                  >
                    {word.from}
                  </span>
                  <ArrowRight size={14} color="#555" />
                  <span
                    style={{
                      color: "#fff",
                      fontWeight: "700",
                      fontFamily: "monospace",
                    }}
                  >
                    {word.to}
                  </span>
                </div>
              ))}
            </div>
          </Section>
        );

      case "review":
        return <WebsiteReview onNavigate={handleReviewNavigation} />;

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0b0c10",
        color: "#e5e7eb",
        fontFamily: "Inter, system-ui, sans-serif",
        display: "flex",
      }}
    >
      <nav
        style={{
          width: "260px",
          borderRight: "1px solid rgba(255,255,255,0.06)",
          padding: "32px 24px",
          display: mobileMenu ? "block" : "none",
          position: "fixed",
          height: "100vh",
          background: "#0b0c10",
          zIndex: 50,
          "@media (min-width: 768px)": { display: "block" },
        }}
        className="sidebar"
      >
        <div
          style={{
            marginBottom: "40px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div
            style={{
              width: "24px",
              height: "24px",
              background: "#22c55e",
              borderRadius: "50%",
              boxShadow: "0 0 15px rgba(34,197,94,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 100 100"
              fill="none"
              stroke="#0b0c10"
              strokeWidth="10"
            >
              <path d="M10 40 L50 10 L90 40" />
              <path d="M25 40 V90 M75 40 V90" />
              <line x1="15" y1="65" x2="85" y2="65" />
            </svg>
          </div>
          <div>
            <div
              style={{
                fontWeight: "800",
                color: "#fff",
                fontSize: "14px",
                letterSpacing: "1px",
              }}
            >
              SIMPLER
            </div>
            <div
              style={{
                fontSize: "10px",
                fontFamily: "monospace",
                color: "#9ca3af",
              }}
            >
              // BRAND_OS
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {BRAND.sections.map((section) => (
            <button
              key={section.id}
              onClick={() => {
                setActiveTab(section.id);
                setMobileMenu(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px",
                borderRadius: "8px",
                border: "none",
                background:
                  activeTab === section.id
                    ? "rgba(255,255,255,0.05)"
                    : "transparent",
                color: activeTab === section.id ? "#fff" : "#9ca3af",
                cursor: "pointer",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: "600",
                transition: "all 0.2s",
              }}
            >
              <span
                style={{
                  color: activeTab === section.id ? "#22c55e" : "inherit",
                }}
              >
                {section.icon}
              </span>
              {section.label}
            </button>
          ))}
        </div>
        <div
          style={{
            marginTop: "auto",
            position: "absolute",
            bottom: "32px",
            fontSize: "11px",
            color: "#555",
          }}
        >
          v{BRAND.version} · Internal Use Only
        </div>
      </nav>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          padding: "16px",
          background: "#0b0c10",
          borderBottom: "1px solid #222",
          display: "flex",
          justifyContent: "space-between",
          zIndex: 40,
        }}
        className="mobile-header"
      >
        <div style={{ fontWeight: "800", color: "#fff" }}>
          SIMPLER // STRATEGY
        </div>
        <button
          onClick={() => setMobileMenu(!mobileMenu)}
          style={{ background: "none", border: "none", color: "#fff" }}
        >
          <Menu />
        </button>
      </div>
      <main
        style={{
          flex: 1,
          padding: "80px 40px",
          maxWidth: "900px",
          margin: "0 auto",
        }}
        className="main-content"
      >
        {renderContent()}
      </main>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); border-radius: 16px; padding: 32px; transition: transform 0.2s; }
        .card:hover { transform: translateY(-2px); border-color: rgba(255,255,255,0.1); }
        .card.highlight { border-color: #22c55e; background: rgba(34,197,94,0.05); }
        .label { font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; color: #9ca3af; margin-bottom: 12px; }
        h3 { font-size: 20px; font-weight: 700; color: #fff; margin-bottom: 8px; }
        p { color: #9ca3af; line-height: 1.6; font-size: 15px; }
        @media (min-width: 768px) { .sidebar { display: block !important; } .mobile-header { display: none !important; } .main-content { margin-left: 260px !important; padding: 80px 60px !important; } }
        @media (max-width: 767px) { .sidebar { display: none; } .main-content { padding: 80px 20px !important; margin-left: 0 !important; } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
